import { Check } from 'lucide-react';
import { ResizedImage } from '@ah/design/mobile';
import type { ChosenSuperconnector } from '@ah/people/shared';
import type { QuestionOption, QuestionNode } from '@ah/questions/shared';
import { MAIA_PNG } from '../assets';

export function extractQuestionAction(copy: string): string {
  return copy
    .replace(/^\s*Would you like me to help you\s+/i, '')
    .replace(/[?.!]\s*$/, '')
    .trim();
}

export function questionNeedsContinue(current: QuestionNode | null, selectedIds: Set<string>): boolean {
  // RADIO auto-advances on tap, so Continue is only needed when the user has
  // selected "Other" (textarea must be confirmed). MULTI always needs Continue.
  if (!current) return false;
  const isMulti = current.kind === 'MULTI';
  const otherSelected = selectedIds.has('__other__');
  return isMulti || (current.allowOther === true && otherSelected);
}

export function QuestionCard({
  godmother,
  rootQuestion,
  current,
  loading,
  done,
  selectedIds,
  otherText,
  onOtherTextChange,
  onSelect,
  onOtherTap,
}: {
  godmother: ChosenSuperconnector | null;
  rootQuestion: string;
  current: QuestionNode | null;
  loading: boolean;
  done: boolean;
  selectedIds: Set<string>;
  otherText: string;
  onOtherTextChange: (s: string) => void;
  onSelect: (opt: QuestionOption) => void;
  onOtherTap: () => void;
}) {
  const pictureUrl = godmother?.pictureUrl ?? MAIA_PNG;
  const alt = godmother?.firstName ?? 'Your Underling';
  const prompt = current?.prompt ?? rootQuestion;
  const isMulti = current?.kind === 'MULTI';
  const otherSelected = selectedIds.has('__other__');

  if (done) {
    return (
      <div className="relative min-h-[240px] flex flex-col items-center justify-center gap-3 py-10">
        <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg shadow-primary/30">
          <Check size={36} strokeWidth={3} />
        </div>
        <p className="text-[15px] font-medium text-foreground/85 text-center max-w-[28ch]">
          Got it — I&apos;ll factor this in.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 pt-2">
        <ResizedImage src={pictureUrl} renderWidth={80} fallback={false} alt={alt} className="self-center w-20 h-20 rounded-full object-cover ring-4 ring-primary/10" />
        <p className="font-bold text-lg leading-snug text-foreground">
          {prompt}
        </p>
        {current && (
          <p className="text-[12px] uppercase tracking-wider font-medium text-muted-foreground/80">
            {isMulti ? 'Pick all that apply' : 'Pick one'}
          </p>
        )}
      </div>

      {loading ? (
        <ul className="space-y-2 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <li key={i} className="h-12 rounded-xl bg-muted" />
          ))}
        </ul>
      ) : !current || current.options.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          Couldn't load options. Skip and try another.
        </p>
      ) : (
        <>
          <ul className="space-y-2">
            {current.options.map((opt) => {
              const on = selectedIds.has(opt.id);
              return (
                <li key={opt.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(opt)}
                    className={[
                      'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-[15px] leading-snug transition-all',
                      on ? 'border-primary bg-primary/5 text-foreground' : 'border-border bg-card text-foreground/85',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'shrink-0 flex items-center justify-center border',
                        isMulti ? 'w-5 h-5 rounded' : 'w-5 h-5 rounded-full',
                        on ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40',
                      ].join(' ')}
                    >
                      {on && (isMulti ? <Check size={13} strokeWidth={3} /> : <span className="w-2 h-2 rounded-full bg-primary-foreground" />)}
                    </span>
                    <span>{opt.label}</span>
                  </button>
                </li>
              );
            })}
            {current.allowOther && (
              <li>
                <button
                  type="button"
                  onClick={onOtherTap}
                  className={[
                    'w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-[15px] leading-snug transition-all',
                    otherSelected ? 'border-primary bg-primary/5 text-foreground' : 'border-border bg-card text-foreground/85',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'shrink-0 flex items-center justify-center border',
                      isMulti ? 'w-5 h-5 rounded' : 'w-5 h-5 rounded-full',
                      otherSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40',
                    ].join(' ')}
                  >
                    {otherSelected && (isMulti ? <Check size={13} strokeWidth={3} /> : <span className="w-2 h-2 rounded-full bg-primary-foreground" />)}
                  </span>
                  <span>Other</span>
                </button>
                {otherSelected && (
                  <textarea
                    value={otherText}
                    onChange={(e) => onOtherTextChange(e.target.value)}
                    placeholder="Tell me more…"
                    rows={2}
                    className="mt-2 w-full rounded-xl border border-border bg-card px-3 py-2 text-[15px] leading-snug resize-none focus:outline-none focus:border-primary/60"
                  />
                )}
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  );
}
